import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  makeStyles,
  TabList,
  Tab,
  mergeClasses
} from "@fluentui/react-components";
import { CloseButton } from "../input/CloseButton";

// Define custom styles using Fluent UI's makeStyles
const useStyles = makeStyles({
  sidebar: {
    width: "100%",
    "@media (min-width: 576px)": {
      width: "400px"
    },
    display: "flex",
    flexDirection: "column",
    height: "100%" // Ensure the sidebar takes full height
  },
  header: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  titleContainer: {
    display: "flex",
    alignItems: "center"
  },
  beforeTitle: {
    marginRight: "8px"
  },
  afterTitle: {
    marginLeft: "8px"
  },
  customTitle: {
    flexGrow: 1
  },
  tabsContainer: {
    borderBottom: "1px solid var(--neutralStroke1)",
    overflow: "hidden"
  },
  content: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "24px"
  },
  footer: {
    padding: "24px",
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid var(--neutralStroke1)"
  }
});

export const Sidebar = forwardRef(function Sidebar(
  {
    title,
    afterTitle,
    children,
    contentClassName,
    className,
    headerClassName,
    tabs,
    currentTab,
    changeTab,
    customTitle,
    footer,
    footerClassName,
    onClose,
    position = "left",
    open = true,
    as = "aside",
    ...rest
  },
  ref // Receive the forwarded ref
) {
  const styles = useStyles();

  return (
    <Drawer
      className={mergeClasses(styles.sidebar, className)}
      open={open}
      onOpenChange={onClose} // Handle open state externally
      modalType="non-modal"
      position={position}
      as={as}
      {...rest}
    >
      <DrawerHeader className={`${styles.header} ${headerClassName}`}>
        {title && <DrawerHeaderTitle action={<CloseButton onClick={onClose} />}>{title}</DrawerHeaderTitle>}
        {customTitle && <div className={styles.customTitle}>{customTitle}</div>}
        {afterTitle && <div className={styles.afterTitle}>{afterTitle}</div>}
      </DrawerHeader>

      {tabs && tabs.length > 0 && (
        <div className={styles.tabsContainer}>
          <TabList selectedValue={currentTab} onTabSelect={(e, data) => changeTab(data.value)}>
            {tabs.map(tab => (
              <Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
        </div>
      )}

      {/* Content */}
      <DrawerBody ref={ref} className={mergeClasses(styles.content, contentClassName)}>
        {children}
      </DrawerBody>

      {/* Footer */}
      {footer && <DrawerFooter className={mergeClasses(styles.footer, footerClassName)}>{footer}</DrawerFooter>}
    </Drawer>
  );
});

Sidebar.propTypes = {
  title: PropTypes.node,
  beforeTitle: PropTypes.node,
  afterTitle: PropTypes.node,
  customTitle: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  disableOverflowScroll: PropTypes.bool,
  headerClassName: PropTypes.string,
  headerContainer: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired
    })
  ),
  currentTab: PropTypes.string,
  changeTab: PropTypes.func,
  footer: PropTypes.node,
  onClose: PropTypes.func,
  position: PropTypes.string,
  footerClassName: PropTypes.string,
  open: PropTypes.bool,
  as: PropTypes.string
};

Sidebar.defaultProps = {
  disableOverflowScroll: false,
  open: true
};
